import type { DemandPartnerBidderConfig } from '../partners/model';
import { IntegrationCustomParameterField } from './model';

const classicFields = [
  IntegrationCustomParameterField.PublisherId,
  IntegrationCustomParameterField.SiteId,
  IntegrationCustomParameterField.PlacementId,
  IntegrationCustomParameterField.AppId,
  IntegrationCustomParameterField.ZoneId,
  IntegrationCustomParameterField.TagId,
  IntegrationCustomParameterField.DisplayManager,
  IntegrationCustomParameterField.TripleliftInventoryCode,
  IntegrationCustomParameterField.OpenxDelDomain,
  IntegrationCustomParameterField.UnrulyTargetingUuid,
  IntegrationCustomParameterField.OpenxUnitId,
  IntegrationCustomParameterField.SmartAdServerPageId,
  IntegrationCustomParameterField.SmartAdServerNetworkId,
  IntegrationCustomParameterField.SmartAdServerFormatId,
] as const;

const restrictedClassicFields: Record<string, IntegrationCustomParameterField[]> = {
  openx: [
    IntegrationCustomParameterField.OpenxUnitId,
    IntegrationCustomParameterField.OpenxDelDomain,
  ],
  appnexus: [
    IntegrationCustomParameterField.TagId,
    IntegrationCustomParameterField.PlacementId,
    IntegrationCustomParameterField.PublisherId,
    IntegrationCustomParameterField.SiteId,
  ],
  smartadserver: [
    IntegrationCustomParameterField.SiteId,
    IntegrationCustomParameterField.SmartAdServerFormatId,
    IntegrationCustomParameterField.SmartAdServerNetworkId,
    IntegrationCustomParameterField.SmartAdServerPageId,
  ],
};

const supportedDynamicFields = new Set<IntegrationCustomParameterField>([
  IntegrationCustomParameterField.ImpExt,
  IntegrationCustomParameterField.GroupId,
  IntegrationCustomParameterField.BidRequestExtCo,
  IntegrationCustomParameterField.BidRequestExt,
]);

export const fallbackIntegrationBidderConfig: DemandPartnerBidderConfig = {
  default: { visible: [], required: [], defaults: {} },
  bidders: {
    colossus: required(IntegrationCustomParameterField.ImpExt),
    colossus_benefitads: required(IntegrationCustomParameterField.ImpExt),
    colossus_bidfuse: required(IntegrationCustomParameterField.ImpExt),
    colossus_cinepulse: required(IntegrationCustomParameterField.ImpExt),
    colossus_pwrads: required(IntegrationCustomParameterField.ImpExt),
    freewheel: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    freewheel_adipolo: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    freewheel_bidfuse: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    freewheel_castify: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    freewheel_cinemoon: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    freewheel_hax: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    freewheel_positive: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    freewheel_silo: required(
      IntegrationCustomParameterField.ImpExt,
      IntegrationCustomParameterField.BidRequestExtCo,
    ),
    improvedigital: visible(
      IntegrationCustomParameterField.BidRequestExt,
      IntegrationCustomParameterField.ImpExt,
    ),
    sharethrough: visible(IntegrationCustomParameterField.ImpExt),
    ttd: visible(IntegrationCustomParameterField.ImpExt),
    snackmedia: visible(IntegrationCustomParameterField.ImpExt),
  },
};

export interface ResolvedIntegrationCustomParameter {
  field: IntegrationCustomParameterField;
  required: boolean;
}

export function resolveIntegrationCustomParameters(
  bidderCode: string | null | undefined,
  config: DemandPartnerBidderConfig,
): ResolvedIntegrationCustomParameter[] {
  const normalizedCode = bidderCode?.trim().toLowerCase() ?? '';
  const classic =
    restrictedClassicFields[normalizedCode] ?? (normalizedCode ? [...classicFields] : []);
  const bidder = config.bidders[normalizedCode];
  const visibleDynamic = new Set([
    ...config.default.visible,
    ...config.default.required,
    ...(bidder?.visible ?? []),
    ...(bidder?.required ?? []),
  ]);
  const requiredDynamic = new Set([...config.default.required, ...(bidder?.required ?? [])]);
  const dynamic = [...visibleDynamic]
    .filter(isSupportedDynamicField)
    .map((field) => ({ field, required: requiredDynamic.has(field) }));
  const classicSet = new Set<IntegrationCustomParameterField>(classic);

  return [
    ...classic.map((field) => ({ field, required: false })),
    ...dynamic.filter(({ field }) => !classicSet.has(field)),
  ];
}

function isSupportedDynamicField(value: string): value is IntegrationCustomParameterField {
  return supportedDynamicFields.has(value as IntegrationCustomParameterField);
}

function visible(...fields: IntegrationCustomParameterField[]) {
  return { visible: fields, required: [], defaults: {} };
}

function required(...fields: IntegrationCustomParameterField[]) {
  return { visible: fields, required: fields, defaults: {} };
}
